import { useDataGridConfig } from '@/hooks/useDataGridConfig'
import { buildFilterParams, type FilterFieldMapping, type SortFieldMapping } from '@/lib/dataGridFilters'
import http from '@/lib/http'
import { u } from '@/lib/url'
import type { Order, OrderListItem, OrderSubscriptionType, ProductItem } from '@/types/order'
import type { GridFilterItem, GridSortModel } from '@mui/x-data-grid'
import { keepPreviousData, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

type GridSortItem = GridSortModel[number]

/**
 * Field mappings for order DataGrid filters.
 * Maps DataGrid column fields to backend filter parameter names.
 */
export const ORDER_FILTER_FIELD_MAPPING: FilterFieldMapping = {
  orderNumber: { paramName: 'orderNumberFilter', type: 'text' },
  billingEmail: { paramName: 'billingEmailFilter', type: 'text' },
  shippingPhone: { paramName: 'shippingPhoneFilter', type: 'text' },
  shippingPostcode: { paramName: 'shippingPostcodeFilter', type: 'text' },
  status: { paramName: 'statusFilter', type: 'text' },
  createdAt: { paramName: 'createdAtFilter', type: 'date' },
  items: { paramName: 'productNameFilter', type: 'text' },
  totalAmount: { paramName: 'totalAmountFilter', type: 'number' },
  userEmail: { paramName: 'userEmailFilter', type: 'text' },

  // Item-level — `status` above is the order status, which has no clinical vocabulary.
  orderItemStatus: { paramName: 'orderItemStatusFilter', type: 'text' },
  csNotes: { paramName: 'csNotesFilter', type: 'text' },
  pharmacistNotes: { paramName: 'pharmacistNotesFilter', type: 'text' },
  patientResponse: { paramName: 'patientResponseFilter', type: 'boolean' }
}

/**
 * Field mappings for order DataGrid sorting.
 * Maps DataGrid column fields to backend sort field names.
 */
export const ORDER_SORT_FIELD_MAPPING: SortFieldMapping = {
  orderNumber: 'order_number',
  status: 'status',
  totalAmount: 'total_amount',
  createdAt: 'created_at'

  // TODO (niemal): Enable this when it exists on the API.
  // deliveryDate: 'delivery_date'
}

export type FetchOrdersParams = {
  searchQuery?: string
  searchMode?: 'contains' | 'startsWith'
  /** JSON-stringified array of search attributes, e.g. `JSON.stringify(['order_number','shipping_phone'])`. */
  searchAttributes?: string
  /** When true, skip the total COUNT query. Response `count` reflects only returned rows. */
  skipCount?: boolean
  skip?: number
  limit?: number

  // Advanced filters (JSON-encoded)
  orderNumberFilter?: string
  billingEmailFilter?: string
  shippingPhoneFilter?: string
  shippingPostcodeFilter?: string
  statusFilter?: string
  createdAtFilter?: string
  productNameFilter?: string
  totalAmountFilter?: string
  userEmailFilter?: string
  orderItemStatusFilter?: string
  csNotesFilter?: string
  pharmacistNotesFilter?: string

  // Boolean filters (not JSON-encoded)
  /** `false` = the order has a FANned item with no patient response recorded on any hold entry. */
  patientResponseFilter?: string

  // Sorting
  sortBy?: 'order_number' | 'created_at' | 'total_amount' | 'status'
  sortByDirection?: 'asc' | 'desc'

  excludeChainOrders?: boolean
  excludePaymentPendingOrders?: boolean
}

export type FetchOrdersResponse = {
  orders: OrderListItem[]
  count: number
}

export type APIOrderShipping = {
  first_name: string | null
  last_name: string | null
  address_1: string | null
  address_2: string | null
  city: string | null
  state: string | null
  postcode: string | null
  country: string | null
  email: string | null
  phone: string | null
}

export type APIOrderBilling = {
  first_name: string | null
  last_name: string | null
  address_1: string | null
  address_2: string | null
  city: string | null
  state: string | null
  postcode: string | null
  country: string | null
  email: string | null
  phone: string | null
}

export type APIOrder = {
  hash: string
  woo_order_id: string | null
  order_number: string
  status: string
  type: string
  total_amount: string
  tax_amount: string | null
  billing_email: string
  user_email: string | null
  created_at: string
  updated_at: string
  payment_method_detail: string | null
  payment_method: string | null
  created_via: string | null
  delivery_date: string | null
  shipping: APIOrderShipping | null
  billing: APIOrderBilling | null
  fulfillment_type: string | null
  items: ProductItem[]
}

export type GetOrdersAPIResponse = {
  orders: APIOrder[]
  count: number
}

/**
 * Transform API order (snake_case) to frontend OrderListItem (camelCase)
 */
function toOrderListItem(apiOrder: APIOrder): OrderListItem {
  return {
    id: apiOrder.hash,
    orderNumber: apiOrder.order_number,
    billingEmail: apiOrder.billing_email,
    userEmail: apiOrder.user_email,
    status: apiOrder.status,
    type: apiOrder.type as OrderSubscriptionType,
    totalAmount: Number.parseFloat(apiOrder.total_amount),
    paymentMethod: apiOrder.payment_method,
    createdAt: apiOrder.created_at,
    deliveryDate: apiOrder.delivery_date,
    itemsCount: apiOrder.items.length,
    items: apiOrder.items,
    shippingPostcode: apiOrder.shipping?.postcode ?? null
  }
}

/**
 * Fetch orders with optional search, filters, and pagination
 * @returns Paginated list of orders with total count
 */
export const fetchOrders = async (
  params: FetchOrdersParams = {},
  signal?: AbortSignal
): Promise<FetchOrdersResponse> => {
  const { orders, count } = (await http.get('/orders', {
    params: { ...params, excludeChainOrders: true, excludePaymentPendingOrders: true },
    signal
  })) as GetOrdersAPIResponse

  return {
    orders: orders.map(toOrderListItem),
    count
  }
}

export type UseOrdersQueryOptions = {
  tableId?: string
  enabled?: boolean
  /** When provided, bypasses DataGrid config and uses these params directly (for compact/search mode). */
  params?: Pick<
    FetchOrdersParams,
    | 'searchQuery'
    | 'searchMode'
    | 'searchAttributes'
    | 'skipCount'
    | 'limit'
    | 'skip'
    | 'shippingPhoneFilter'
    | 'shippingPostcodeFilter'
  >
}

/**
 * Hook to fetch orders with DataGrid integration.
 * Supports search query, column filters, sorting, and pagination.
 *
 * When `params` is provided, bypasses DataGrid config (pagination, filters, sorting)
 * and queries directly with the given params — useful for dashboard search.
 */
export const useOrdersQuery = ({
  tableId = 'orders',
  enabled = true,
  params: overrideParams
}: UseOrdersQueryOptions = {}) => {
  const dataGridConfig = useDataGridConfig({ tableId, syncToUrl: !overrideParams })

  const queryParams: FetchOrdersParams = useMemo(() => {
    if (overrideParams) return overrideParams

    const { paginationModel, filters, filterModel, sortModel } = dataGridConfig

    const params: FetchOrdersParams = {
      skip: paginationModel ? paginationModel.page * paginationModel.pageSize : 0,
      limit: paginationModel?.pageSize ?? 10
    }

    const filterParams = buildFilterParams({
      searchQuery: filterModel?.quickFilterValues?.join(' '),
      filterItems: filters as GridFilterItem[],
      sortItems: (sortModel ?? []) as GridSortItem[],
      filterFieldMapping: ORDER_FILTER_FIELD_MAPPING,
      sortFieldMapping: ORDER_SORT_FIELD_MAPPING
    })

    // Keep the quick-filter search surface aligned with the dashboard preview, so
    // a "View all" from a postcode/email/name hit lands on the same matching rows instead
    // of the backend-default (order_number + billing_email) narrower search.
    const searchAugment: Pick<FetchOrdersParams, 'searchAttributes' | 'searchMode'> = filterParams.searchQuery
      ? {
          searchAttributes: JSON.stringify(['order_number', 'billing_email', 'shipping_postcode', 'billing_full_name']),
          searchMode: 'startsWith'
        }
      : {}

    return { ...params, ...filterParams, ...searchAugment }
  }, [overrideParams, dataGridConfig])

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['orders', queryParams],
    queryFn: ({ signal }) => fetchOrders(queryParams, signal),
    enabled,
    placeholderData: keepPreviousData
  })

  const orders = useMemo(
    () =>
      (data?.orders ?? []).map(order => ({
        ...order,
        createdAt: new Date(order.createdAt),

        // TODO (niemal): Expect this to come from the API.
        deliveryDate: order.deliveryDate ? new Date(order.deliveryDate) : null
      })),
    [data?.orders]
  )

  // With `skipCount`, the backend doesn't run the total COUNT query, so `data.count`
  // reflects only returned rows. Derive from the array length locally so callers get
  // a consistent meaning ("rows we have") regardless of backend response shape.
  const count = queryParams.skipCount ? orders.length : (data?.count ?? 0)

  return {
    orders,
    count,
    isLoading,
    isFetching,
    isError,
    error,
    dataGridConfig
  }
}

/**
 * Resolves order numbers to order hashes in a single batched query, for linking to
 * order pages from contexts that only carry numbers (e.g. the patient files feed).
 *
 * Numbers that don't come back stay unmapped and callers should degrade to plain
 * text: `fetchOrders` always excludes CHAIN and payment-pending orders, so those
 * would never resolve here (in practice neither kind carries files).
 */
export const useOrderHashesByNumberQuery = (orderNumbers: string[]) => {
  // Deduped and sorted so the cache key is stable regardless of input order.
  const numbers = useMemo(() => [...new Set(orderNumbers)].sort(), [orderNumbers])

  const { data } = useQuery({
    queryKey: ['orders', 'hash-by-number', numbers],
    queryFn: ({ signal }) =>
      fetchOrders(
        {
          orderNumberFilter: JSON.stringify({ operator: 'isAnyOf', values: numbers }),
          limit: numbers.length,
          skipCount: true
        },
        signal
      ),
    enabled: numbers.length > 0,
    // An order's hash never changes once issued, so this is safe to hold on to.
    staleTime: 5 * 60 * 1000
  })

  return useMemo(() => {
    const hashByNumber = new Map<string, string>()
    // `OrderListItem.id` is the order hash.
    for (const order of data?.orders ?? []) {
      if (order.orderNumber) hashByNumber.set(order.orderNumber, order.id)
    }
    return hashByNumber
  }, [data?.orders])
}

/**
 * Transform API order (snake_case) to frontend Order (camelCase) for the detail page.
 */
function toOrderDetails(apiOrder: APISingleOrder): Order {
  return {
    hash: apiOrder.hash,
    wooOrderId: apiOrder.woo_order_id,
    orderNumber: apiOrder.order_number,
    status: apiOrder.status,
    userDisplayName: apiOrder.user_display_name ?? '',
    subscriptionType: apiOrder.type as OrderSubscriptionType,
    itemCount: apiOrder.items?.length ?? 0,
    totalAmount: Number.parseFloat(apiOrder.total_amount),
    taxAmount: apiOrder.tax_amount ? Number.parseFloat(apiOrder.tax_amount) : null,
    billingEmail: apiOrder.billing_email,
    userEmail: apiOrder.user_email,
    paymentMethod: apiOrder.payment_method,
    paymentMethodDetail: apiOrder.payment_method_detail,
    createdVia: apiOrder.created_via,
    shippingMethod: apiOrder.shipping_method ?? '',
    deliveryDate: apiOrder.delivery_date,
    fulfillmentType: apiOrder.fulfillment_type,
    shipping: apiOrder.shipping
      ? {
          type: '',
          firstName: apiOrder.shipping.first_name ?? '',
          lastName: apiOrder.shipping.last_name ?? '',
          address1: apiOrder.shipping.address_1 ?? '',
          address2: apiOrder.shipping.address_2 ?? '',
          city: apiOrder.shipping.city ?? '',
          county: apiOrder.shipping.state ?? '',
          postCode: apiOrder.shipping.postcode ?? '',
          country: apiOrder.shipping.country ?? '',
          email: apiOrder.shipping.email ?? undefined,
          phone: apiOrder.shipping.phone ?? undefined
        }
      : {
          type: '',
          firstName: '',
          lastName: '',
          address1: '',
          address2: '',
          city: '',
          county: '',
          postCode: '',
          country: ''
        },
    billing: apiOrder.billing
      ? {
          type: '',
          firstName: apiOrder.billing.first_name ?? '',
          lastName: apiOrder.billing.last_name ?? '',
          address1: apiOrder.billing.address_1 ?? '',
          address2: apiOrder.billing.address_2 ?? '',
          city: apiOrder.billing.city ?? '',
          county: apiOrder.billing.state ?? '',
          postCode: apiOrder.billing.postcode ?? '',
          country: apiOrder.billing.country ?? '',
          email: apiOrder.billing.email ?? undefined,
          phone: apiOrder.billing.phone ?? undefined
        }
      : {
          type: '',
          firstName: '',
          lastName: '',
          address1: '',
          address2: '',
          city: '',
          county: '',
          postCode: '',
          country: ''
        },
    items: apiOrder.items,
    cancellationReason: apiOrder.cancellation_reason,
    startedAt: new Date(apiOrder.created_at),
    updatedAt: new Date(apiOrder.updated_at),
    amountRefunded: apiOrder.amount_refunded ?? undefined,
    totalAvailableToRefund: apiOrder.total_available_to_refund ?? undefined,
    refundHistory: apiOrder.refund_history?.map(item => ({
      date: item.date,
      amount: item.amount,
      reason: item.reason,
      method: item.method,
      status: item.status
    }))
  }
}

// Refund history item from API (matches refundHistoryItemSchema)
export type APIRefundHistoryItem = {
  date: string
  amount: string
  reason: string
  method: string
  status: 'completed' | 'failed'
}

// Extended order details from single order endpoint (matches orderDetailsSchema)
export type APISingleOrder = APIOrder & {
  user_display_name: string | null
  shipping_method: string | null
  cancellation_reason: string | null
  amount_refunded: number | null
  total_available_to_refund: number | null
  refund_history: APIRefundHistoryItem[]
}

/**
 * Fetch a single order by hash.
 */
export const fetchOrderByHash = async (hash: string): Promise<Order> => {
  const order = (await http.get(`/orders/${hash}`)) as APISingleOrder
  return toOrderDetails(order)
}

/**
 * Suspense query hook for fetching a single order by hash.
 */
export const useOrderByHashQuery = (hash: string) => {
  return useSuspenseQuery({
    queryKey: ['order', hash],
    queryFn: () => fetchOrderByHash(hash)
  })
}

// Royal Mail Shipment Package types (matches backend RoyalMailShipmentPackageWithId)
export type ShipmentPackageClaim = {
  status:
    | 'claimable'
    | 'not_claimable'
    | 'claim_submitted'
    | 'claim_approved'
    | 'claim_rejected'
    | 'claim_expired'
    | 'claim_exported'
    | null
  type: 'lost' | 'delayed' | null
  windowStart: string | null
  windowEnd: string | null
  submittedAt: string | null
  exportedAt: string | null
}

export type ShipmentPackage = {
  _id: string
  orderId: string
  carrierDetails: {
    uniqueId: string
  }
  shipmentId: string
  packageOccurrence: number
  trackingNumber: string
  carrierTrackingUrl: string
  hasBeenDelivered: boolean
  createdAt: string
  archivedAt: string | null
  estimatedShippingTime: string | null
  trackingFetchedAt: string | null
  trackingErrorCode: string | null
  trackingErrorDescription: string | null
  trackingStatus: string | null
  lastEventName: string | null
  lastEventDateTime: string | null
  deliveryDate: string | null
  serviceCode: string | null
  serviceName: string | null
  firstName: string | null
  lastName: string | null
  address: string | null
  address2: string | null
  address3: string | null
  city: string | null
  postcode: string | null
  contentsCostPrice: number | null
  shipmentWeight: number | null
  claim: ShipmentPackageClaim | null
  expectedDeliveryDate: string | null
}

/**
 * Hook to fetch Royal Mail shipment packages by order ID.
 */
export const useShipmentPackagesByOrderIdQuery = (orderId: string) => {
  return useQuery({
    queryKey: ['shipment-packages', orderId],
    queryFn: async () => {
      const res = await http.get<ShipmentPackage[]>('/royal-mail/shipments', {
        params: {
          orderId,
          includeArchived: true
        }
      })

      return res.data.filter(pkg => !pkg.archivedAt || pkg.hasBeenDelivered)
    },
    enabled: !!orderId
  })
}

// Royal Mail label response type (matches backend RoyalMailPackageWithLabel)
export type ShipmentLabel = {
  Label: string // Base64-encoded label
  LabelFormat: 'PDF' | 'PNG'
}

/**
 * Print Royal Mail shipping labels for an order.
 */
export const printOrderLabels = async (orderId: string): Promise<ShipmentLabel[]> => {
  return (await http.get(u`/royal-mail/print-order-labels/${orderId}`)) as ShipmentLabel[]
}

/**
 * Mutation hook for printing Royal Mail shipping labels.
 */
export const usePrintOrderLabelsMutation = () => {
  return useMutation({
    mutationFn: printOrderLabels
  })
}

export const createShipmentLabel = async (hash: string): Promise<void> => {
  await http.post(u`/orders/${hash}/create-shipment-label`, {})
}

export const useCreateShipmentMutation = () => {
  return useMutation({
    mutationFn: createShipmentLabel
  })
}

export const cancelShipment = async (orderId: string): Promise<void> => {
  await http.delete(u`/royal-mail/shipments/${orderId}`)
}

export const useCancelShipmentMutation = () => {
  return useMutation({
    mutationFn: cancelShipment
  })
}

export const useSendToTitan = () => {
  return useMutation({
    mutationKey: ['send-to-titan'],
    mutationFn: async ({ orderNumber }: { orderNumber: string }) =>
      http.post(
        '/titan/prescriptions',
        { orderNumber },
        {
          headers: {
            'x-evaro-source': 'api'
          }
        }
      )
  })
}

/**
 * Update an order's status.
 * @param hash - Order hash
 * @param status - New status in `wc-*` format (e.g. `wc-pharmacist-check`)
 */
export const updateOrderStatus = async (hash: string, status: string): Promise<void> => {
  await http.patch(`/orders/${hash}/status`, { status })
}

/**
 * Mutation hook for updating order status.
 */
export const useUpdateOrderStatusMutation = () => {
  return useMutation({
    mutationFn: ({ hash, status }: { hash: string; status: string }) => updateOrderStatus(hash, status)
  })
}

export type BillingShippingFields = {
  first_name?: string
  last_name?: string
  address_1?: string
  address_2?: string
  city?: string
  postcode?: string
  country?: string
  state?: string
  email?: string
  phone?: string
}

export type UpdateOrderBillingShippingBody = {
  billing?: BillingShippingFields
  shipping?: BillingShippingFields
}

export const useUpdateOrderBillingShippingMutation = (hash: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateOrderBillingShippingBody) => http.patch(`/orders/${hash}/billing-shipping`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', hash] })
    }
  })
}

/**
 * Shipping methods an order can be moved onto from the order page.
 *
 * Home delivery only, mirroring the allow list the API enforces. The collect methods need
 * a pickup point that the order page has no way to choose, and Royal Mail rejects a PUDO
 * shipment without one.
 */
export const CHANGEABLE_SHIPPING_METHOD_IDS = [
  'ep_royal_mail_24',
  'ep_royal_mail_48',
  'ep_royal_mail_next_day',
  'ep_free_shipping'
] as const

export type ChangeableShippingMethodId = (typeof CHANGEABLE_SHIPPING_METHOD_IDS)[number]

export type ShippingMethod = {
  method_id: string
  title: string
  description: string
  cost: number
  dates: string
}

export type ChangeableShippingMethod = ShippingMethod & { method_id: ChangeableShippingMethodId }

const isChangeableShippingMethod = (method: ShippingMethod): method is ChangeableShippingMethod =>
  (CHANGEABLE_SHIPPING_METHOD_IDS as readonly string[]).includes(method.method_id)

/**
 * The shipping methods CS can move an order onto.
 *
 * The list is the same for every order and changes only on deploy, so it is cached for the
 * session rather than refetched per order.
 */
export const useChangeableShippingMethodsQuery = () => {
  return useQuery({
    queryKey: ['shipping-methods', 'changeable'],
    queryFn: async () => {
      const methods = (await http.get('/prescriber/shipping-methods')) as ShippingMethod[]

      return methods.filter(isChangeableShippingMethod)
    },
    staleTime: Number.POSITIVE_INFINITY
  })
}

/**
 * Move an order onto a different shipping method.
 *
 * The cost already charged is carried over, so the order total does not change. The new
 * method is picked up by the next shipping label generated for the order.
 */
export const useUpdateOrderShippingMethodMutation = (hash: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (shippingMethod: ChangeableShippingMethodId) =>
      http.patch(`/orders/${hash}/shipping-method`, { shipping_method: shippingMethod }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', hash] })
      // The write lands a `fields_update` note, so the order timeline is now stale too.
      queryClient.invalidateQueries({ queryKey: ['timeline', 'order', hash] })
    }
  })
}

/**
 * Update why an order was cancelled. An empty string clears the stored reason.
 */
export const useUpdateOrderCancellationReasonMutation = (hash: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (cancellationReason: string) =>
      http.patch(`/orders/${hash}/cancellation-reason`, { cancellation_reason: cancellationReason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', hash] })
      // The write lands a `fields_update` note, so the order timeline is now stale too.
      queryClient.invalidateQueries({ queryKey: ['timeline', 'order', hash] })
    }
  })
}
